import React, {useEffect, useRef, useState} from 'react';
import {Template} from "../../../../components/template/template.jsx";
import EcapWysiwyg from "../components/ecap-wysiwyg.jsx";
import {useLoaderData, useParams} from "react-router";
import {navigateHook} from "../../../../hooks/use-navigate.js";
import projectUtils from "../../../../utils/project-utils.js";
import ecapTemplateService from "../../../../services/web/ecap-template-service.js";
import {Modal} from "../../../../components/modal/modal.jsx";
import InputForm from "../../../../components/input/input-form.jsx";
import Button from "../../../../components/button/button.jsx";
import {useQuery} from "@tanstack/react-query";
import corporationService from "../../../../services/web/corporation-service.js";
import {toast} from "react-toastify";

const EcapTemplate = () => {
    const {id} = useParams();
    const navigate = navigateHook();

    const loaderData = useLoaderData();

    const [isLoading, setIsLoading] = useState(false);

    const [content, setContent] = useState("");
    const [headerTemplateSelected, setHeaderTemplateSelected] = useState(-1);
    const [name, setName] = useState("")
    const [applyModal, setApplyModal] = useState(false);

    const editorRef = useRef(undefined);

    const {data: headerTemplate} = useQuery({
        queryFn: () => corporationService.headerTemplates(),
        queryKey: ["headerTemplates"]
    });

    const {data, isSuccess, refetch} = useQuery({
        queryFn: () => ecapTemplateService.get(id),
        queryKey: ["ecap-template"],
        enabled: !!id
    });

    useEffect(() => {
        if (!isSuccess) return;

        const {content, headerTemplateId, name} = data;

        setContent(content);
        setHeaderTemplateSelected(headerTemplateId);
        setName(name);
    }, [data, isSuccess]);

    const translateErrMessage = (message, defaultMessage = 'Não foi possível salvar.') => {
        const err = {"name.already.used": "Este \"Nome\" já está sendo utilizado."}
        return err[message] ?? defaultMessage
    }

    const apply = () => {
        if (!name) {
            projectUtils.swalErrorMessage("", "O campo \"Nome do modelo\" é obrigatório.", "Ok");
            return
        }

        setIsLoading(true);

        const html = editorRef.current.getHTML();

        const formData = new FormData();
        formData.append("headerTemplateId", headerTemplateSelected)
        formData.append("name", name)

        const formBlob = new Blob([html], {
            type: 'application/octet-stream'
        });

        formData.append("content", formBlob);

        let response;
        if (id) {
            formData.append("id", id);
            response = ecapTemplateService.update(formData)
        }
        else response = ecapTemplateService.create(formData)

        response
            .then((data) => {
                if (id) {
                    refetch();
                    toast.success("Salvo com sucesso");
                    setTimeout(() => setApplyModal(false), 1000);
                    return;
                }

                toast.success("Salvo com sucesso");
                setTimeout(() => setApplyModal(false), 1000);
                navigate(`/ecap-template/form/${data}`);
            })
            .catch(async error => {
                const {message} = error.response.data;

                let errMessage = message ?? "Não foi possível salvar.";

                if(errMessage === "name.already.used") errMessage = "Este \"Nome do modelo\" já está sendo utilizado."

                projectUtils.swalErrorMessage("", errMessage, "Ok");
            })
            .finally(() => setIsLoading(false))
    }

    const deleteEcap = () => {
        projectUtils.swalMessage(
            'warning',
            `Excluir o modelo de ${loaderData?.ecap_acronym}`,
            `Deseja continuar? Essa ação não poderá ser desfeita.`,
            'Sim',
            true,
            'Não',
            ({isConfirmed}) => {
                if (!isConfirmed) return;

                setIsLoading(true);
                ecapTemplateService.delete(id)
                    .then(() => {
                        toast.success("Excluído com sucesso");
                        navigate("/ecap");
                    })
                    .catch(async error => {
                        console.error(error);
                        projectUtils.swalErrorMessage('', 'Não foi possível excluir.', 'Ok');
                    })
                    .finally(() => setIsLoading(false));
            }
        );
    }

    const updateHeader = (headerId) => {
        if (editorRef.current.can().undo() || editorRef.current.can().redo()) {
            projectUtils.swalMessage(
                'warning',
                'Atenção',
                'Ao confirmar, o histórico de alterações será perdido. Deseja continuar?',
                'Sim',
                true,
                'Não',
                ({isConfirmed}) => {
                    if (!isConfirmed) return;
                    setHeaderTemplateSelected(headerId)
                    setContent(editorRef.current.getHTML());
                }
            )
        } else {
            setHeaderTemplateSelected(headerId)
            setContent(editorRef.current.getHTML());
        }
    }

    return (
        <>
            <Template isLoading={isLoading}>
                <Template.Header>
                    <Template.Title className={"flex items-center gap-2"}>
                        {loaderData?.ecap_acronym}
                    </Template.Title>
                </Template.Header>

                <Template.Body>
                    <EcapWysiwyg
                        id={id}
                        content={content}
                        editorRef={editorRef}
                        apply={() => setApplyModal(true)}
                        updateAndGenPdf={false}
                        deleteEcap={deleteEcap}
                        updateHeader={updateHeader}
                        headerTemplate={headerTemplate}
                        headerTemplateSelected={headerTemplate?.find(item => item.id === headerTemplateSelected)}
                        data={data}
                        ecapName={loaderData?.ecap_name}
                        showHistory={false}
                        setIsLoading={setIsLoading}
                        enableDeleteActionFeedback
                    />
                </Template.Body>
            </Template>

            <Modal open={applyModal} setOpen={setApplyModal} title={id ? "Alterar" : "Inserir" + " nome"} className={"w-6/12"}>
                <Modal.Body>
                    <InputForm
                        id={"name"}
                        value={name}
                        onChange={({target: {value}}) => setName(value)}
                        label={"Nome do modelo *"}
                    />
                </Modal.Body>
                <Modal.Footer>
                    <Button
                        onClick={apply}
                        actionFeedbackType='save'
                        loadingClassName="!text-gray-900 !opacity-100"
                        successClassName="!bg-green-700 !text-white !opacity-100"
                    >

                        Salvar
                    </Button>
                </Modal.Footer>
            </Modal>
        </>
    );
};

export default EcapTemplate;